import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import TransactionStatusBadge from './TransactionStatusBadge';
import TransactionTimeline from './TransactionTimeline';

const TransactionTable = ({ transactions, onExport }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const toggleRowExpansion = (transactionId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(transactionId)) {
      newExpanded?.delete(transactionId);
    } else {
      newExpanded?.add(transactionId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRiskScoreColor = (score) => {
    if (score <= 30) return 'text-success';
    if (score <= 70) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Table Header */}
      <div className="px-6 py-4 border-b border-border bg-muted/50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Transaction History</h3>
          <Button
            variant="outline"
            onClick={onExport}
            iconName="Download"
            iconPosition="left"
            iconSize={16}
          >
            Export
          </Button>
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('id')}
                  className="flex items-center space-x-1 hover:text-foreground"
                >
                  <span>Transaction ID</span>
                  <Icon name={getSortIcon('id')} size={12} />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Recipient
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('amount')}
                  className="flex items-center space-x-1 hover:text-foreground"
                >
                  <span>Amount</span>
                  <Icon name={getSortIcon('amount')} size={12} />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Exchange Rate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('date')}
                  className="flex items-center space-x-1 hover:text-foreground"
                >
                  <span>Date</span>
                  <Icon name={getSortIcon('date')} size={12} />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {transactions?.map((transaction) => (
              <React.Fragment key={transaction?.id}>
                <tr className="hover:bg-muted/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-foreground">
                      {transaction?.id}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {transaction?.reference}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-foreground">
                      {transaction?.recipient?.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {transaction?.recipient?.bank}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-foreground">
                      {formatCurrency(transaction?.sendAmount, transaction?.sendCurrency)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      → {formatCurrency(transaction?.receiveAmount, transaction?.receiveCurrency)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-foreground">
                      {transaction?.exchangeRate}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Fee: {formatCurrency(transaction?.fee, transaction?.sendCurrency)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <TransactionStatusBadge status={transaction?.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {formatDate(transaction?.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Button
                      variant="ghost"
                      onClick={() => toggleRowExpansion(transaction?.id)}
                      iconName={expandedRows?.has(transaction?.id) ? "ChevronUp" : "ChevronDown"}
                      iconSize={16}
                    >
                      Details
                    </Button>
                  </td>
                </tr>
                
                {/* Expanded Row Details */}
                {expandedRows?.has(transaction?.id) && (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 bg-muted/20">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Transaction Timeline */}
                        <div>
                          <TransactionTimeline timeline={transaction?.timeline} />
                        </div>
                        
                        {/* Compliance & Risk Information */}
                        <div className="space-y-4">
                          <div>
                            <h4 className="font-medium text-foreground mb-3">Compliance Information</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Risk Score:</span>
                                <span className={`font-medium ${getRiskScoreColor(transaction?.riskScore)}`}>
                                  {transaction?.riskScore}/100
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">AML Status:</span>
                                <span className={transaction?.amlStatus === 'cleared' ? 'text-success' : 'text-warning'}>
                                  {transaction?.amlStatus}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">PEP Check:</span>
                                <span className={transaction?.pepStatus === 'cleared' ? 'text-success' : 'text-warning'}>
                                  {transaction?.pepStatus}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Source of Funds:</span>
                                <span className="text-foreground">{transaction?.sourceOfFunds}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Purpose:</span>
                                <span className="text-foreground">{transaction?.purpose}</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Audit Trail */}
                          <div>
                            <h4 className="font-medium text-foreground mb-3">Audit Trail</h4>
                            <div className="space-y-1 text-xs text-muted-foreground">
                              {transaction?.auditTrail?.map((entry, index) => (
                                <div key={index} className="flex justify-between">
                                  <span>{entry?.action}</span>
                                  <span>{formatDate(entry?.timestamp)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card Layout */}
      <div className="lg:hidden divide-y divide-border">
        {transactions?.map((transaction) => (
          <div key={transaction?.id} className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1">
                <div className="text-sm font-medium text-foreground">
                  {transaction?.id}
                </div>
                <div className="text-xs text-muted-foreground">
                  {transaction?.recipient?.name}
                </div>
              </div>
              <TransactionStatusBadge status={transaction?.status} size="sm" />
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
              <div>
                <span className="text-muted-foreground">Amount:</span>
                <div className="font-medium text-foreground">
                  {formatCurrency(transaction?.sendAmount, transaction?.sendCurrency)}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Date:</span>
                <div className="text-foreground">
                  {formatDate(transaction?.createdAt)}
                </div>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={() => toggleRowExpansion(transaction?.id)}
              iconName={expandedRows?.has(transaction?.id) ? "ChevronUp" : "ChevronDown"}
              iconPosition="right"
              iconSize={16}
              fullWidth
            >
              {expandedRows?.has(transaction?.id) ? 'Hide Details' : 'View Details'}
            </Button>
            
            {/* Mobile Expanded Details */}
            {expandedRows?.has(transaction?.id) && (
              <div className="mt-4 pt-4 border-t border-border space-y-4">
                <TransactionTimeline timeline={transaction?.timeline} />
                
                <div>
                  <h4 className="font-medium text-foreground mb-2">Compliance Info</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Risk Score:</span>
                      <span className={getRiskScoreColor(transaction?.riskScore)}>
                        {transaction?.riskScore}/100
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Source:</span>
                      <span className="text-foreground">{transaction?.sourceOfFunds}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TransactionTable;